import { createReducer, on } from '@ngrx/store';
import { JobCardDto } from '../../../../api/gse/models/job-card-dto';
import { JobCardDetailsDto } from '../../../../api/gse/models/job-card-details-dto';
import * as JobCardActions from './job-card.actions';

export interface JobCardState {
  dailyCheckins: JobCardDto[];
  refuelingJobs: JobCardDto[];
  otherJobs: JobCardDto[];
  dailyCheckinsTotal: number;
  refuelingJobsTotal: number;
  otherJobsTotal: number;
  dailyCheckinsTotalPages: number;
  refuelingJobsTotalPages: number;
  otherJobsTotalPages: number;
  dailyCheckinsCurrentPage: number;
  refuelingJobsCurrentPage: number;
  otherJobsCurrentPage: number;
  dailyCheckinsLoading: boolean;
  refuelingJobsLoading: boolean;
  otherJobsLoading: boolean;
  jobCards: JobCardDto[];
  jobCardsTotal: number;
  jobCardsTotalPages: number;
  jobCardsCurrentPage: number;
  jobCardsLoading: boolean;
  isRefuelingLogSubmitting: boolean;
  refuelingLogSuccess: boolean;
  selectedDate: number;
  selectedMonthYear: Date;
  selectedJobCard: JobCardDetailsDto | null;
  isSelectedJobCardLoading: boolean;
  error: string | null;
}

export const initialState: JobCardState = {
  dailyCheckins: [],
  refuelingJobs: [],
  otherJobs: [],
  dailyCheckinsTotal: 0,
  refuelingJobsTotal: 0,
  otherJobsTotal: 0,
  dailyCheckinsTotalPages: 0,
  refuelingJobsTotalPages: 0,
  otherJobsTotalPages: 0,
  dailyCheckinsCurrentPage: 0,
  refuelingJobsCurrentPage: 0,
  otherJobsCurrentPage: 0,
  dailyCheckinsLoading: false,
  refuelingJobsLoading: false,
  otherJobsLoading: false,
  jobCards: [],
  jobCardsTotal: 0,
  jobCardsTotalPages: 0,
  jobCardsCurrentPage: 0,
  jobCardsLoading: false,
  isRefuelingLogSubmitting: false,
  refuelingLogSuccess: false,
  selectedDate: new Date().getDate(),
  selectedMonthYear: new Date(),
  selectedJobCard: null,
  isSelectedJobCardLoading: false,
  error: null,
};

export const jobCardReducer = createReducer(
  initialState,
  // Daily Checkins
  on(JobCardActions.loadDailyCheckins, (state) => ({
    ...state,
    dailyCheckinsLoading: true,
    dailyCheckins: [],
    dailyCheckinsTotal: 0,
    dailyCheckinsTotalPages: 0,
    dailyCheckinsCurrentPage: 0,
    error: null,
  })),
  on(JobCardActions.loadDailyCheckinsSuccess, (state, { data, totalRecords, totalPages, currentPage }) => ({
    ...state,
    dailyCheckinsLoading: false,
    dailyCheckins: data,
    dailyCheckinsTotal: totalRecords,
    dailyCheckinsTotalPages: totalPages,
    dailyCheckinsCurrentPage: currentPage,
  })),
  on(JobCardActions.loadMoreDailyCheckins, (state) => ({
    ...state,
    dailyCheckinsLoading: true,
  })),
  on(JobCardActions.loadMoreDailyCheckinsSuccess, (state, { data, totalRecords, totalPages, currentPage }) => ({
    ...state,
    dailyCheckinsLoading: false,
    dailyCheckins: [...state.dailyCheckins, ...data],
    dailyCheckinsTotal: totalRecords,
    dailyCheckinsTotalPages: totalPages,
    dailyCheckinsCurrentPage: currentPage,
  })),
  on(JobCardActions.loadDailyCheckinsFailure, (state, { error }) => ({
    ...state,
    dailyCheckinsLoading: false,
    error,
  })),
  // Refueling Jobs
  on(JobCardActions.loadRefuelingJobs, (state) => ({
    ...state,
    refuelingJobsLoading: true,
    refuelingJobs: [],
    refuelingJobsTotal: 0,
    refuelingJobsTotalPages: 0,
    refuelingJobsCurrentPage: 0,
    error: null,
  })),
  on(JobCardActions.loadRefuelingJobsSuccess, (state, { data, totalRecords, totalPages, currentPage }) => ({
    ...state,
    refuelingJobsLoading: false,
    refuelingJobs: data,
    refuelingJobsTotal: totalRecords,
    refuelingJobsTotalPages: totalPages,
    refuelingJobsCurrentPage: currentPage,
  })),
  on(JobCardActions.loadMoreRefuelingJobs, (state) => ({
    ...state,
    refuelingJobsLoading: true,
  })),
  on(JobCardActions.loadMoreRefuelingJobsSuccess, (state, { data, totalRecords, totalPages, currentPage }) => ({
    ...state,
    refuelingJobsLoading: false,
    refuelingJobs: [...state.refuelingJobs, ...data],
    refuelingJobsTotal: totalRecords,
    refuelingJobsTotalPages: totalPages,
    refuelingJobsCurrentPage: currentPage,
  })),
  on(JobCardActions.loadRefuelingJobsFailure, (state, { error }) => ({
    ...state,
    refuelingJobsLoading: false,
    error,
  })),
  // Other Jobs
  on(JobCardActions.loadOtherJobs, (state) => ({
    ...state,
    otherJobsLoading: true,
    otherJobs: [],
    otherJobsTotal: 0,
    otherJobsTotalPages: 0,
    otherJobsCurrentPage: 0,
    error: null,
  })),
  on(JobCardActions.loadOtherJobsSuccess, (state, { data, totalRecords, totalPages, currentPage }) => ({
    ...state,
    otherJobsLoading: false,
    otherJobs: data,
    otherJobsTotal: totalRecords,
    otherJobsTotalPages: totalPages,
    otherJobsCurrentPage: currentPage,
  })),
  on(JobCardActions.loadMoreOtherJobs, (state) => ({
    ...state,
    otherJobsLoading: true,
  })),
  on(JobCardActions.loadMoreOtherJobsSuccess, (state, { data, totalRecords, totalPages, currentPage }) => ({
    ...state,
    otherJobsLoading: false,
    otherJobs: [...state.otherJobs, ...data],
    otherJobsTotal: totalRecords,
    otherJobsTotalPages: totalPages,
    otherJobsCurrentPage: currentPage,
  })),
  on(JobCardActions.loadOtherJobsFailure, (state, { error }) => ({
    ...state,
    otherJobsLoading: false,
    error,
  })),
  // General Job Cards
  on(JobCardActions.loadJobCards, (state) => ({
    ...state,
    jobCardsLoading: true,
    jobCardsTotal: 0,
    jobCardsTotalPages: 0,
    jobCardsCurrentPage: 0,
    error: null,
  })),
  on(JobCardActions.loadJobCardsSuccess, (state, { data, totalRecords, totalPages, currentPage }) => ({
    ...state,
    jobCardsLoading: false,
    jobCards: data,
    jobCardsTotal: totalRecords,
    jobCardsTotalPages: totalPages,
    jobCardsCurrentPage: currentPage,
  })),
  on(JobCardActions.loadJobCardsFailure, (state, { error }) => ({
    ...state,
    jobCardsLoading: false,
    error,
  })),
  // Create Refueling Log
  on(JobCardActions.createRefuelingLog, (state) => ({
    ...state,
    isRefuelingLogSubmitting: true,
    refuelingLogSuccess: false,
    error: null,
  })),
  on(JobCardActions.createRefuelingLogSuccess, (state) => ({
    ...state,
    isRefuelingLogSubmitting: false,
    refuelingLogSuccess: true,
  })),
  on(JobCardActions.createRefuelingLogFailure, (state, { error }) => ({
    ...state,
    isRefuelingLogSubmitting: false,
    refuelingLogSuccess: false,
    error,
  })),
  on(JobCardActions.resetRefuelingLogStatus, (state) => ({
    ...state,
    refuelingLogSuccess: false,
    error: null,
  })),
  on(JobCardActions.setSelectedDate, (state, { day, monthYear }) => ({
    ...state,
    selectedDate: day,
    selectedMonthYear: monthYear,
  })),
  // Get Job Card By Id
  on(JobCardActions.loadJobCardById, (state, { silent }) => ({
    ...state,
    isSelectedJobCardLoading: silent ? state.isSelectedJobCardLoading : true,
    selectedJobCard: silent ? state.selectedJobCard : null,
    error: null,
  })),
  on(JobCardActions.loadJobCardByIdSuccess, (state, { response }) => {
    const jobCard = Array.isArray(response.data) ? response.data[0] : response.data;
    return {
      ...state,
      isSelectedJobCardLoading: false,
      selectedJobCard: jobCard as JobCardDetailsDto,
    };
  }),
  on(JobCardActions.loadJobCardByIdFailure, (state, { error }) => ({
    ...state,
    isSelectedJobCardLoading: false,
    error,
  }))
);
